import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from '@treams/treams-routes';
import { Callout } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import Modal, {
	ModalBackground,
	ModalContent,
} from 'packages/kakapo-ui-kit/components/modals/Modal';
import ModalHeader, {
	ModalHeaderTitle,
} from 'packages/kakapo-ui-kit/components/modals/ModalHeader';
import { LinkTo } from 'packages/kakapo-ui-kit/types/Link';
import ImageUploadDropZone from 'packages/kakapo-ui-kit/components/forms/controls/image-upload/ImageUpload';
import { ImageFile, IImageSpecification } from 'packages/kakapo-ui-kit/types/Image';
import ImageResizer from 'packages/kakapo-ui-kit/components/forms/controls/image-upload/ImageResizer';
import FormErrorMessage from 'packages/kakapo-ui-kit/components/forms/errors/FormErrorMessage';
import useUploadAsset from 'packages/kakapo-ui-application/hooks/useUploadAsset';
import GraphQLErrorMessage from 'packages/kakapo-ui-kit/components/forms/errors/GraphQLErrorMessage';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';

export function UserImageUploadModalContents(props: {
	userId: string;
	returnUrl?: LinkTo;
	imageSpecification: IImageSpecification;
}) {
	const { t } = useTranslation();
	const history = useHistory();

	const [uploadedFile, setUploadedFile] = useState<ImageFile | undefined>(undefined);
	const [resizedFileBlob, setResizedFileBlob] = useState<Blob | undefined>(undefined);
	const [error, setError] = useState<any | undefined>(undefined);
	const [fileTooBig, setFileTooBig] = useState(false);
	const [success, setSuccess] = useState(false);

	const [uploadAsset, { loading, error: graphQLError }] = useUploadAsset(async (assetID) => {
		if (props.imageSpecification.key === 'avatars') {
			await updateUserAvatar({
				variables: {
					input: { userID: props.userId, assetID },
				},
			});
		} else if (props.imageSpecification.key === 'banners') {
			await updateUserCover({
				variables: {
					input: { userID: props.userId, assetID },
				},
			});
		}
	});

	const [updateUserAvatar] = graphql.useUpdateUserAvatarMutation();
	const [updateUserCover] = graphql.useUpdateUserCoverMutation();

	const onFinishImage = async () => {
		if (!resizedFileBlob) {
			return;
		}

		// If selection or file is bigger than 20mb
		if (resizedFileBlob.size > 20971520) {
			setFileTooBig(true);

			return;
		}

		setFileTooBig(false);
		await uploadAsset(resizedFileBlob);
		setUploadedFile(undefined);
		setSuccess(true);

		if (props.returnUrl) {
			history.push(props.returnUrl);
		}
	};

	if (loading) {
		return <Spinner isLoading size="18px" />;
	}

	return (
		<>
			<GraphQLErrorMessage error={graphQLError} />
			{error && <FormErrorMessage error={error} />}

			{fileTooBig && (
				<Callout mb={2} title={t('personalSettings.imageUpload.fileTooBig')} color="red" />
			)}

			{success && (
				<Callout title={t('personalSettings.imageUpload.success')} color="green" mb={2} />
			)}

			{uploadedFile == null ? (
				<ImageUploadDropZone
					accept={['image/png', 'image/jpeg', 'image/bmp']}
					translationPropertyKey={props.imageSpecification.key}
					onError={setError}
					onFileSelected={setUploadedFile}
				/>
			) : (
				<ImageResizer
					fileString={uploadedFile as string}
					imageSpecification={props.imageSpecification}
					onImageResize={setResizedFileBlob}
					onFinish={onFinishImage}
					onError={setError}
				/>
			)}
		</>
	);
}

export default function UserImageUploadModal(props: {
	userId: string;
	returnUrl: LinkTo;
	imageSpecification: IImageSpecification;
}) {
	const { t } = useTranslation();

	return (
		<ModalBackground>
			<Modal>
				<ModalHeader returnUrl={props.returnUrl}>
					<ModalHeaderTitle>
						{t('common.personalInfo.images.uploadNew')}
						&nbsp;
						{props.imageSpecification.title}
					</ModalHeaderTitle>
				</ModalHeader>

				<ModalContent>
					<UserImageUploadModalContents
						userId={props.userId}
						returnUrl={props.returnUrl}
						imageSpecification={props.imageSpecification}
					/>
				</ModalContent>
			</Modal>
		</ModalBackground>
	);
}
